<script>
// The root is a Teleport, so attrs can't fall through on their own. Forwarding
// them to the panel (v-bind="$attrs" below) is what lets consumers add classes
// and listeners to it.
export default { inheritAttrs: false }
</script>

<script setup>
import { ref } from 'vue'

// The chrome every anchored menu shares: a teleported surface, an uppercase
// micro-label header, a loading overlay, and the `.dropdown-item` /
// `.dropdown-divider` / `.dropdown-section` vocabulary consumers fill the slot
// with. Positioning belongs to the host (see useAnchoredMenu) — this only draws.

defineProps({
  show: {
    type: Boolean,
    default: false
  },
  headerLabel: {
    type: String,
    default: null
  },
  headerSubtext: {
    type: String,
    default: null
  },
  loading: {
    type: Boolean,
    default: false
  },
  loadingText: {
    type: String,
    default: 'Loading...'
  },
  // From useAnchoredMenu.
  menuStyle: {
    type: Object,
    default: () => ({})
  }
})

const rootEl = ref(null)

// The host needs the element for positioning and click-outside.
defineExpose({ el: rootEl })
</script>

<template>
  <Teleport to="body">
    <Transition name="dropdown-panel">
      <div
        v-if="show"
        ref="rootEl"
        class="dropdown-panel"
        :style="menuStyle"
        v-bind="$attrs"
      >
        <div v-if="loading" class="dropdown-panel-loading">
          <div class="spinner"></div>
          <span>{{ loadingText }}</span>
        </div>

        <div v-if="headerLabel" class="dropdown-panel-header">
          <span class="header-label">{{ headerLabel }}</span>
          <span v-if="headerSubtext" class="header-subtext">{{ headerSubtext }}</span>
        </div>

        <div class="dropdown-panel-content" :class="{ 'is-loading': loading }">
          <slot></slot>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<!-- Unscoped: the panel is teleported out of this component's tree, and slot
     markup carries the *consumer's* scope attribute, not ours. Everything is
     namespaced under .dropdown-panel so nothing leaks. -->
<style>
.dropdown-panel {
  background: var(--surface-page);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  z-index: 9999;
  overflow: hidden;
  min-width: 200px;
}

.dropdown-panel-header {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  padding: 0.85rem 1.25rem 0.35rem;
}

.dropdown-panel-header .header-label {
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-muted);
  white-space: nowrap;
}

.dropdown-panel-header .header-subtext {
  font-size: 0.75rem;
  color: var(--text-faint);
}

.dropdown-panel-content {
  position: relative;
  padding: 0.25rem 0 0.5rem;
}

.dropdown-panel-content.is-loading {
  opacity: 0.3;
  pointer-events: none;
}

/* Loading overlay */
.dropdown-panel-loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--surface-page);
  opacity: 0.95;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  z-index: 10;
  color: var(--text-muted);
  font-size: 0.85rem;
}

.dropdown-panel-loading .spinner {
  width: 24px;
  height: 24px;
  border: 2px solid var(--text-disabled);
  border-top: 2px solid var(--accent);
  border-radius: var(--radius-pill);
  animation: dropdown-panel-spin 1s linear infinite;
}

@keyframes dropdown-panel-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Item vocabulary — inset pills matching the main sidebar dropdown items. */
.dropdown-panel .dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 0 0.5rem;
  padding: 0.6rem 0.75rem;
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: var(--transition-fast);
  color: var(--text-primary);
  font-size: 0.9rem;
}

.dropdown-panel .dropdown-item:hover {
  background: var(--accent-soft-3);
  color: var(--text-heading);
}

.dropdown-panel .dropdown-item .item-icon {
  color: var(--text-secondary);
  width: 1.2rem;
  font-size: 1rem;
  flex-shrink: 0;
}

.dropdown-panel .dropdown-divider {
  height: 1px;
  background: var(--surface-overlay-2);
  margin: 0.5rem 0.75rem;
}

.dropdown-panel .dropdown-section {
  padding: 0.5rem 1.25rem 0.25rem;
  font-size: 0.7rem;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 600;
}

/* Segmented choice row (e.g. the JSONL/CSV toggle every export menu offers). */
.dropdown-panel .format-toggle {
  display: flex;
  gap: 0.25rem;
  padding: 0.5rem 1rem;
}

.dropdown-panel .format-btn {
  flex: 1;
  padding: 0.35rem 0.5rem;
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--text-faint);
  font-size: 0.75rem;
  cursor: pointer;
  transition: var(--transition-fast);
}

.dropdown-panel .format-btn:hover {
  border-color: var(--accent-soft-5);
  color: var(--text-primary);
}

.dropdown-panel .format-btn.active {
  background: var(--accent-soft-4);
  border-color: var(--accent-soft-6);
  color: var(--text-primary);
}

/* Enter/leave. The panel fades in where it lands — no directional motion, since
   useAnchoredMenu picks the side at open time and any axis we animated along
   would only be a guess at it. Opacity, not var(--transition): `all` would also
   animate the top/left in menuStyle, sliding the panel on every reposition. */
.dropdown-panel-enter-active,
.dropdown-panel-leave-active {
  transition: opacity 0.2s ease;
}

.dropdown-panel-enter-from,
.dropdown-panel-leave-to {
  opacity: 0;
}
</style>
